import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "./query-keys";

interface CreatePropertyPurchaseParams {
  registrationId: string | number;
  purchasePrice: number;
  developer: string;
  fullAddress: string;
  purchasedPropertyType?: string;
  purchasedPropertyCharacteristics?: string;
}

interface UpdatePropertyPurchaseParams {
  id: number;
  purchasePrice?: number;
  developer?: string;
  fullAddress?: string;
  purchasedPropertyType?: string;
  purchasedPropertyCharacteristics?: string;
}

interface PropertyPurchaseResponse {
  property_purchase: {
    id: number;
    registration_id: number;
    purchase_price: number;
    developer: string;
    full_address: string;
    purchased_property_type: string | null;
    purchased_property_characteristics: string | null;
    coupon_ready_at: string | null;
    coupons_printed_at: string | null;
    created_at: string;
    updated_at: string;
  };
}

export function useCreatePropertyPurchase() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      registrationId,
      purchasePrice,
      developer,
      fullAddress,
      purchasedPropertyType,
      purchasedPropertyCharacteristics,
    }: CreatePropertyPurchaseParams) => {
      const response = await authenticatedFetch(
        `/api/admin/registrations/${registrationId}/property-purchases`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            purchasePrice,
            developer,
            fullAddress,
            purchasedPropertyType,
            purchasedPropertyCharacteristics,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create property purchase: ${response.statusText}`);
      }

      return response.json() as Promise<PropertyPurchaseResponse>;
    },
    onSuccess: (_data, variables) => {
      // Invalidate registration detail queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.registrations.detail(variables.registrationId),
      });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.stats });
    },
  });
}

export function useUpdatePropertyPurchase() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      purchasePrice,
      developer,
      fullAddress,
      purchasedPropertyType,
      purchasedPropertyCharacteristics,
    }: UpdatePropertyPurchaseParams) => {
      const response = await authenticatedFetch(`/api/admin/property-purchases/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          purchasePrice,
          developer,
          fullAddress,
          purchasedPropertyType,
          purchasedPropertyCharacteristics,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update property purchase: ${response.statusText}`);
      }

      return response.json() as Promise<PropertyPurchaseResponse>;
    },
    onSuccess: () => {
      // Invalidate all registration detail queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail() });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.stats });
    },
  });
}

export function useDeletePropertyPurchase() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await authenticatedFetch(`/api/admin/property-purchases/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`Failed to delete property purchase: ${response.statusText}`);
      }

      return response.json() as Promise<{ ok: boolean }>;
    },
    onSuccess: () => {
      // Invalidate all registration detail queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail() });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.stats });
    },
  });
}
